import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus, FaSave, FaTimes } from "react-icons/fa";
import Spinner from "../../components/Spinner";
import http from "../../api/apiClient";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";

type Schedule = { enabled: boolean; times: string[] };

const ENDPOINT = "/finance/settings/eboekhouden/schedule";
const MAX_TIMES = 8;

/** At which times of day payments are fetched from e-Boekhouden automatically (Dutch time). */
export default function ImportSchedule({ canManage }: { canManage: boolean }): ReactElement {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(true);
  const [times, setTimes] = useState<string[]>([]);
  const [newTime, setNewTime] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const apply = (data: Schedule | null) => {
    setEnabled(data?.enabled !== false);
    setTimes(Array.isArray(data?.times) ? data!.times : []);
  };

  useEffect(() => {
    let cancelled = false;
    http.get(ENDPOINT)
      .then(({ data }) => {
        if (!cancelled) apply(data);
      })
      .catch((caught) => {
        if (!cancelled) setMessage({ ok: false, text: mapHttpErrorToMessage(caught, t).message || "" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const addTime = () => {
    if (!/^\d{2}:\d{2}$/.test(newTime) || times.includes(newTime) || times.length >= MAX_TIMES) return;
    setTimes((current) => [...current, newTime].sort());
    setNewTime("");
  };

  const errorText = (caught: any) => {
    switch (caught?.response?.data?.message) {
      case "SCHEDULE_TIMES_REQUIRED": return t("finance.settings.schedule.errors.required", { defaultValue: "Choose at least one time, or switch automatic fetching off." });
      case "SCHEDULE_TOO_MANY_TIMES": return t("finance.settings.schedule.errors.too_many", { defaultValue: "At most eight times a day." });
      case "SCHEDULE_TIME_INVALID": return t("finance.settings.schedule.errors.invalid", { defaultValue: "A time is not valid." });
      default: return mapHttpErrorToMessage(caught, t).message || "";
    }
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await http.put(ENDPOINT, { enabled, times });
      apply(data);
      setMessage({ ok: true, text: t("finance.settings.schedule.saved", { defaultValue: "Schedule saved." }) });
    } catch (caught) {
      setMessage({ ok: false, text: errorText(caught) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:p-6">
      <h2 className="text-base font-bold text-slate-950 dark:text-white">
        {t("finance.settings.schedule.title", { defaultValue: "Fetch automatically" })}
      </h2>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {t("finance.settings.schedule.help", {
          defaultValue: "Payments are fetched from e-Boekhouden at these times (Dutch time) and linked where possible. Every fetch is in the connection log. Fetching by hand stays possible.",
        })}
      </p>
      {loading ? (
        <Spinner />
      ) : (
        <>
          <label className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
            <input type="checkbox" checked={enabled} disabled={!canManage} onChange={(event) => setEnabled(event.target.checked)} />
            {t("finance.settings.schedule.enabled", { defaultValue: "Fetch automatically" })}
          </label>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {times.length === 0 && (
              <span className="text-sm text-slate-500">{t("finance.settings.schedule.no_times", { defaultValue: "No times chosen." })}</span>
            )}
            {times.map((time) => (
              <span key={time} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm font-semibold tabular-nums ${enabled ? "border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200" : "border-slate-200 bg-slate-50 text-slate-500 dark:border-white/10 dark:bg-white/5"}`}>
                {time}
                {canManage && (
                  <button
                    type="button"
                    onClick={() => setTimes((current) => current.filter((other) => other !== time))}
                    aria-label={t("finance.settings.schedule.remove", { time, defaultValue: "Remove {{time}}" })}
                    className="rounded-full p-0.5 hover:bg-white/80 dark:hover:bg-white/10"
                  >
                    <FaTimes aria-hidden="true" className="text-xs" />
                  </button>
                )}
              </span>
            ))}
          </div>
          {canManage && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                type="time"
                value={newTime}
                onChange={(event) => setNewTime(event.target.value)}
                aria-label={t("finance.settings.schedule.new_time", { defaultValue: "New time" })}
                className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm dark:border-white/10 dark:bg-slate-900 dark:text-white"
              />
              <button
                type="button"
                onClick={addTime}
                disabled={!newTime || times.length >= MAX_TIMES}
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5"
              >
                <FaPlus aria-hidden="true" />
                {t("finance.settings.schedule.add", { defaultValue: "Add time" })}
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-indigo-600 px-4 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <FaSave aria-hidden="true" />
                {t("finance.settings.schedule.save", { defaultValue: "Save schedule" })}
              </button>
            </div>
          )}
        </>
      )}
      {message && (
        <p role={message.ok ? "status" : "alert"} className={`mt-3 text-sm font-semibold ${message.ok ? "text-emerald-700 dark:text-emerald-300" : "text-red-600"}`}>
          {message.text}
        </p>
      )}
    </section>
  );
}
